import React, { useEffect, useState } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// 계약별 SMS 발송 설정 (상세보기 하단)
//  - 공동중개 시 우리측(임대/임차) 지정 → 미설정이면 발송 보류
//  - 일정별 발송 on/off, 임대인/임차인 개별 선택
//  - 해당 계약의 발송 이력 조회
// ─────────────────────────────────────────────

const TYPE_LABEL = {
  deposit: '계약금', middle: '중도금', balance: '잔금', expiry: '임대차 만료',
};

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const fmtDate = (d) => (d ? String(d).split('T')[0] : '');

// 발송 상태 표시
//  reminder_sent=1 이어도 실패했을 수 있으므로 마지막 이력(last_status)으로 구분한다
//  이력이 없는데 완료 표시면 기한 경과로 건너뛴 경우다
const sentBadge = (r) => {
  if (!r.reminder_sent) return null;
  if (r.last_status === 'success') return { text: '✅ 발송완료', color: '#4CAF50' };
  if (r.last_status === 'fail')    return { text: '⚠️ 발송실패', color: '#dc2626' };
  return { text: '⏭️ 기한경과', color: '#94a3b8' };
};

export default function ContractSmsPanel({ contractId, contract, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [ourSide, setOurSide] = useState(contract?.our_side || 'both');
  const [msg, setMsg] = useState('');
  // 즉시 발송 확인 팝업: null이면 닫힘, 배열이면 선택된 schedule_id 목록
  const [pickOpen, setPickOpen] = useState(false);
  const [picked, setPicked] = useState([]);

  // 중개사무소가 2곳 이상이면 공동중개
  const brokerCount = Array.isArray(contract?.broker_info) ? contract.broker_info.length : 1;
  const isCoBroker = brokerCount > 1;

  // 일정별 기본 체크 상태 계산
  //  단독중개(both) → 관리탭 규칙을 그대로 따름
  //  공동중개       → 규칙과 무관하게 우리 담당측만 체크 (상대측 오발송 방지)
  //  미설정(unset)  → 둘 다 해제
  const defaults = (r) => {
    if (ourSide === 'landlord') return { landlord: true,  tenant: false };
    if (ourSide === 'tenant')   return { landlord: false, tenant: true  };
    if (ourSide === 'unset')    return { landlord: false, tenant: false };
    // both = 단독중개 → 관리탭 규칙 사용 (규칙이 없으면 양측 발송)
    return {
      landlord: r?.rule_landlord === undefined || r?.rule_landlord === null ? true : !!r.rule_landlord,
      tenant:   r?.rule_tenant   === undefined || r?.rule_tenant   === null ? true : !!r.rule_tenant,
    };
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const load = async () => {
    try {
      const [t, l] = await Promise.all([
        axios.get(`/api/contract-sms/targets/${contractId}`, auth()),
        axios.get(`/api/contract-sms/logs?contract_id=${contractId}`, auth()),
      ]);
      // 계약금(deposit)은 문자 발송 대상이 아니므로 목록에서 제외
      if (t.data.success) setRows(t.data.data.filter(r => r.event_type !== 'deposit'));
      if (l.data.success) setLogs(l.data.data);
    } catch (e) {
      setMsg('설정을 불러오지 못했습니다');
    }
  };

  // 개별 설정을 지워 관리탭 규칙을 다시 따르게 한다
  const resetRow = async (scheduleId) => {
    if (!window.confirm('이 일정의 개별 설정을 지우고 관리탭 규칙을 따르도록 되돌립니다.')) return;
    try {
      await axios.delete(`/api/contract-sms/targets/${scheduleId}`, auth());
      setMsg('✅ 규칙을 따르도록 되돌렸습니다');
      load();
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg('❌ 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  // 발송완료 표시를 해제해 다시 보낼 수 있게 한다 (이력은 보존)
  const retry = async (scheduleId) => {
    if (!window.confirm('이 일정을 다시 보낼 수 있도록 준비합니다. 계속할까요?')) return;
    try {
      await axios.post(`/api/contract-sms/retry/${scheduleId}`, {}, auth());
      setMsg('✅ 재발송 준비되었습니다');
      load();
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg('❌ 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  // 체크 즉시 저장한다 (저장 버튼을 누르지 않아 발송이 누락되는 것을 방지)
  const change = async (idx, field, value) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [field]: value };
    setRows(next);

    const r = next[idx];
    try {
      await axios.put(`/api/contract-sms/targets/${r.schedule_id}`, {
        contract_id: contractId,
        // 방금 바꾼 항목만 값을 확정하고, 나머지는 기존 상태를 그대로 둔다
        //  (null 이면 관리탭 규칙을 계속 따른다)
        send_landlord: field === 'send_landlord' ? value : r.send_landlord,
        send_tenant:   field === 'send_tenant'   ? value : r.send_tenant,
        enabled: 1,
      }, auth());
      setMsg('✅ 저장되었습니다');
      setTimeout(() => setMsg(''), 1500);
    } catch (e) {
      setMsg('❌ 저장 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  const saveRow = async (r) => {
    try {
      await axios.put(`/api/contract-sms/targets/${r.schedule_id}`, {
        contract_id: contractId,
        send_landlord: r.send_landlord === null ? (defaults(r).landlord ? 1 : 0) : r.send_landlord,
        send_tenant:   r.send_tenant   === null ? (defaults(r).tenant   ? 1 : 0) : r.send_tenant,
        enabled: r.enabled === 0 ? 0 : 1,
      }, auth());
      setMsg('✅ 저장되었습니다');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('❌ 저장 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  const saveOurSide = async () => {
    try {
      await axios.put(`/api/contract-sms/our-side/${contractId}`, { our_side: ourSide }, auth());
      setMsg('✅ 담당측이 저장되었습니다');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('❌ 저장 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  // 이 계약의 '발송' 체크된 미발송 일정을 지금 즉시 보낸다 (발송 시점 무시)
  // 발송 확인 팝업 열기 (기본 선택은 전부 해제 — 실수 방지)
  const openPicker = () => {
    setPicked([]);
    setPickOpen(true);
  };

  const togglePick = (id) => {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 선택한 일정만 즉시 발송
  const runNow = async () => {
    if (picked.length === 0) {
      setMsg('발송할 일정을 선택하세요');
      return;
    }
    if (!window.confirm(`선택한 ${picked.length}건에 지금 실제로 문자를 보냅니다.\n계속할까요?`)) return;

    setMsg('발송 중...');
    setPickOpen(false);
    try {
      const res = await axios.post(
        `/api/contract-sms/run-now/${contractId}`,
        { scheduleIds: picked },
        auth()
      );
      setMsg(res.data.message || '실행 완료');
      load();   // 이력·상태 갱신
    } catch (e) {
      setMsg('❌ 실행 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  return (
    <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <strong>📨 SMS 발송 설정</strong>
        <span style={{ fontSize: '12px', color: '#666' }}>{open ? '▲ 접기' : '▼ 펼치기'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '12px' }}>
          {/* ─── 공동중개 담당측 ─── */}
          {isCoBroker && (
            <div style={{
              padding: '10px', marginBottom: '12px', borderRadius: '4px',
              backgroundColor: ourSide === 'unset' ? '#fff3e0' : '#f1f8e9',
              border: '1px solid #e2e8f0',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px' }}>
                🤝 공동중개 ({brokerCount}곳) — 우리 담당측
              </div>
              {ourSide === 'unset' && (
                <div style={{ fontSize: '12px', color: '#e65100', marginBottom: '6px' }}>
                  ⚠️ 담당측 미설정 상태입니다. 설정 전까지 이 계약의 문자는 발송되지 않습니다.
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select
                  value={ourSide}
                  onChange={(e) => setOurSide(e.target.value)}
                  style={{ padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }}
                >
                  <option value="unset">선택하세요 (발송 보류)</option>
                  <option value="landlord">임대(매도)측 담당</option>
                  <option value="tenant">임차(매수)측 담당</option>
                  <option value="both">양측 모두</option>
                </select>
                {(
                  <button
                    onClick={saveOurSide}
                    style={{
                      backgroundColor: '#2563eb', color: 'white', border: 'none',
                      padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                    }}
                  >
                    저장
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ─── 일정별 발송 설정 ─── */}
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px' }}>일정별 발송</div>
          {rows.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#999' }}>등록된 일정이 없습니다.</div>
          ) : rows.map((r, i) => (
            <div key={r.schedule_id} style={{
              padding: '8px', marginBottom: '6px', borderRadius: '4px',
              backgroundColor: r.rule_enabled ? '#fafafa' : '#f8f8f8',
              border: '1px solid #eee', fontSize: '12px',
              opacity: r.rule_enabled ? 1 : 0.65,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <strong>{TYPE_LABEL[r.event_type] || r.event_type}</strong>
                  {' · '}{fmtDate(r.event_date)}
                  {/* 관리탭에서 사용을 끈 종류는 발송되지 않으므로 알려준다 */}
                  {!r.rule_enabled && (
                    <span style={{ color: '#f59e0b', marginLeft: '6px', fontSize: '11px' }}>
                      ⚠️ 관리탭에서 사용 안 함
                    </span>
                  )}
                  {/* 성공/실패/기한경과를 구분해 표시 */}
                  {(() => {
                    const b = sentBadge(r);
                    return b ? (
                      <span style={{ color: b.color, marginLeft: '6px', fontWeight: 'bold' }}>
                        {b.text}
                      </span>
                    ) : null;
                  })()}
                </span>
                {/* 발송이 끝난 건은 재발송 준비 버튼을 제공 */}
                {r.reminder_sent ? (
                  <button
                    onClick={() => retry(r.schedule_id)}
                    style={{
                      backgroundColor: '#f59e0b', color: 'white', border: 'none',
                      padding: '4px 10px', borderRadius: '3px',
                      cursor: 'pointer', fontSize: '11px',
                    }}
                  >
                    🔄 재발송
                  </button>
                ) : null}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '6px', alignItems: 'center' }}>
                {/* null = 전역 규칙을 따름 */}
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={r.send_landlord === null ? defaults(r).landlord : !!r.send_landlord}
                    onChange={(e) => change(i, 'send_landlord', e.target.checked ? 1 : 0)}
                    style={{ marginRight: '4px' }}
                  />
                  임대(매도)인
                </label>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={r.send_tenant === null ? defaults(r).tenant : !!r.send_tenant}
                    onChange={(e) => change(i, 'send_tenant', e.target.checked ? 1 : 0)}
                    style={{ marginRight: '4px' }}
                  />
                  임차(매수)인
                </label>
                {/* 개별 설정이 저장된 경우에만 되돌리기 제공 */}
                {(r.send_landlord !== null || r.send_tenant !== null) && (
                  <button
                    onClick={() => resetRow(r.schedule_id)}
                    title="관리탭 규칙을 따르도록 되돌리기"
                    style={{
                      marginLeft: 'auto', backgroundColor: 'transparent',
                      border: '1px solid #cbd5e1', borderRadius: '3px',
                      cursor: 'pointer', fontSize: '11px', padding: '2px 8px',
                      color: '#64748b',
                    }}
                  >
                    ↺ 규칙대로
                  </button>
                )}

              </div>
            </div>
          ))}

          {/* ─── 즉시 발송 ─── */}
          <button
            onClick={openPicker}
            style={{
              marginTop: '12px', width: '100%', backgroundColor: '#dc2626',
              color: 'white', border: 'none', padding: '9px', borderRadius: '4px',
              cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
            }}
          >
            ⚡ 지금 발송 (실제 전송)
          </button>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', textAlign: 'center' }}>
            발송 시점과 무관하게 체크된 일정을 즉시 전송합니다
          </div>

          {/* ─── 발송 이력 ─── */}
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '14px', marginBottom: '6px' }}>
            발송 이력 ({logs.length}건)
          </div>
          {logs.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#999' }}>발송 이력이 없습니다.</div>
          ) : logs.map((l) => (
            <div key={l.log_id} style={{
              fontSize: '11px', padding: '6px', marginBottom: '4px',
              backgroundColor: '#f8fafc', borderRadius: '3px',
              borderLeft: `3px solid ${l.status === 'success' ? '#4CAF50' : '#f44336'}`,
            }}>
              <div>
                {TYPE_LABEL[l.event_type] || l.event_type}
                {' · '}{l.recipient_name} ({l.recipient_phone})
                {' · '}{l.status === 'success' ? '성공' : '실패'}
              </div>
              <div style={{ color: '#64748b', marginTop: '2px' }}>
                {String(l.sent_at).replace('T', ' ').slice(0, 16)}
              </div>
              {l.error_msg && (
                <div style={{ color: '#dc2626', marginTop: '2px' }}>{l.error_msg}</div>
              )}
            </div>
          ))}

          {msg && (
            <p style={{ fontSize: '11px', color: '#64748b', marginTop: '10px', textAlign: 'center' }}>
              {msg}
            </p>
          )}
        </div>
      )}

      {/* ─── 즉시 발송 대상 선택 팝업 ───
          체크된 일정만 골라 보낸다. 기본은 전부 해제(실수 방지).
          이미 발송된 일정은 하단에 음영으로 표시만 한다. */}
      {pickOpen && (
        <div
          onClick={() => setPickOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '8px', padding: '20px',
              width: '90%', maxWidth: '520px', maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            <h3 style={{ margin: '0 0 6px 0', fontSize: '16px' }}>
              발송할 일정을 선택하세요
            </h3>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '14px' }}>
              선택한 일정만 지금 즉시 전송됩니다.
            </div>

            {/* 미발송 일정 */}
            {rows.filter((r) => !r.reminder_sent).length === 0 ? (
              <div style={{ fontSize: '13px', color: '#999', padding: '10px' }}>
                발송 가능한 일정이 없습니다.
              </div>
            ) : rows.filter((r) => !r.reminder_sent).map((r) => {
              const left = Math.round(
                (new Date(fmtDate(r.event_date)) - new Date(fmtDate(new Date().toISOString()))) / 86400000
              );
              const toLandlord = r.send_landlord === null ? defaults(r).landlord : !!r.send_landlord;
              const toTenant   = r.send_tenant   === null ? defaults(r).tenant   : !!r.send_tenant;
              const who = [toLandlord && '임대(매도)인', toTenant && '임차(매수)인']
                .filter(Boolean).join(', ') || '수신자 없음';

              return (
                <label
                  key={r.schedule_id}
                  style={{
                    display: 'block', padding: '10px', marginBottom: '8px',
                    border: `1px solid ${picked.includes(r.schedule_id) ? '#2563eb' : '#e2e8f0'}`,
                    borderRadius: '4px', cursor: 'pointer',
                    backgroundColor: picked.includes(r.schedule_id) ? '#eff6ff' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={picked.includes(r.schedule_id)}
                      onChange={() => togglePick(r.schedule_id)}
                    />
                    <strong style={{ fontSize: '13px' }}>
                      {TYPE_LABEL[r.event_type] || r.event_type}
                    </strong>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {fmtDate(r.event_date)}
                    </span>
                    <span style={{
                      marginLeft: 'auto', fontSize: '11px',
                      color: left === 0 ? '#f44336' : '#666',
                    }}>
                      {left === 0 ? '오늘' : left > 0 ? `D-${left}` : `D+${-left}`}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', paddingLeft: '24px' }}>
                    수신: {who}
                  </div>
                </label>
              );
            })}

            {/* 발송 완료 일정 (음영 처리, 선택 불가) */}
            {rows.filter((r) => r.reminder_sent).length > 0 && (
              <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>
                  발송 완료
                </div>
                {rows.filter((r) => r.reminder_sent).map((r) => (
                  <div
                    key={r.schedule_id}
                    style={{
                      padding: '8px 10px', marginBottom: '5px', borderRadius: '4px',
                      backgroundColor: '#f1f5f9', color: '#94a3b8', fontSize: '12px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                    }}
                  >
                    <span>{TYPE_LABEL[r.event_type] || r.event_type}</span>
                    <span>{fmtDate(r.event_date)}</span>
                    <span style={{ marginLeft: 'auto' }}>✅ 발송 완료</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '8px',
              marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee',
            }}>
              <button
                onClick={() => setPickOpen(false)}
                style={{
                  backgroundColor: '#757575', color: 'white', border: 'none',
                  padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                }}
              >
                취소
              </button>
              <button
                onClick={runNow}
                disabled={picked.length === 0}
                style={{
                  backgroundColor: picked.length === 0 ? '#ccc' : '#dc2626',
                  color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px',
                  cursor: picked.length === 0 ? 'not-allowed' : 'pointer', fontSize: '13px',
                }}
              >
                선택 {picked.length}건 발송
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
